import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getAccessToken } from '../../utils/userStorage';

// Define a service using a base URL and expected endpoints

export const api = createApi({
	reducerPath: 'accountAPI',
	baseQuery: fetchBaseQuery({
		baseUrl: `${import.meta.env.VITE_APP_ACCOUNT_API_URL}`,
		prepareHeaders: headers => {
			const accessToken = getAccessToken();
			headers.set('content-type', 'application/json');
			if (accessToken) {
				headers.set('authorization', `Bearer ${accessToken}`);
			}
			return headers;
		}
	}),
	tagTypes: ['Account'],
	endpoints: builder => ({
		getProfileInfo: builder.query({
			query: () => `/profile/`,
			keepUnusedDataFor: 0
		}),
		getOrderHistory: builder.query({
			query: payload => `/orderHistory/${payload}/`
		}),
		updateProfileInfo: builder.mutation({
			query: payload => ({
				url: `/updateProfileInfo/`,
				method: 'PATCH',
				body: payload
			})
		}),
		updateAccountInfo: builder.mutation({
			query: payload => ({
				url: '/updateAccountInfo/',
				method: 'PATCH',
				body: payload as AccountPayload
			})
		}),
		getFavoritesInfo: builder.query({
			query: id => `/favorites/${id}`
		}),
		updateFavoritesInfo: builder.mutation({
			query: data => {
				return {
					url: '/favorites/',
					method: 'PATCH',
					body: data
				};
			}
		}),

		addCard: builder.mutation({
			query: payload => ({
				url: '/addCard/',
				method: 'PATCH',
				body: payload
			})
		}),

		removeCard: builder.mutation({
			query: payload => ({
				url: '/removeCard/',
				method: 'PATCH',
				body: payload
			})
		}),
		setAsPrimaryCard: builder.mutation({
			query: payload => ({
				url: '/defaultCard/',
				method: 'PATCH',
				body: payload
			})
		}),
		combineCard: builder.mutation({
			query: payload => ({
				url: '/combineCard/',
				method: 'PATCH',
				body: payload
			})
		}),
		removeReward: builder.mutation({
			query: payload => ({
				url: '/removeReward/',
				method: 'PATCH',
				body: payload
			})
		})
	})
});

export type AccountPayload = {
	id: string | number;
	email: string;
	phone: string;
	birthday_promotion_medium: string;
	eward_subscription: string;
};

export type FavoriteStorePayload = {
	id: string | number;
	favorite_stores: Array<FavoriteStore>;
};

export type FavoriteStore = {
	store: number;
	priority?: number;
};

export type FavoriteProductPayload = {
	id: string | number;
	favorite_products: Array<FavoriteProduct>;
};

export type FavoriteProduct = {
	product: string;
};

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetProfileInfoQuery,
	useGetOrderHistoryQuery,
	useGetFavoritesInfoQuery,
	useLazyGetProfileInfoQuery,
	useUpdateFavoritesInfoMutation,
	useUpdateAccountInfoMutation,
	useUpdateProfileInfoMutation,
	useAddCardMutation,
	useRemoveCardMutation,
	useRemoveRewardMutation,
	useSetAsPrimaryCardMutation,
	useCombineCardMutation
} = api;
